<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionSearchInput from 'dashboard/components-next/Campaigns/KaptionSearchInput.vue';
import KaptionFilterSelect from 'dashboard/components-next/Campaigns/KaptionFilterSelect.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: FLOW templates are sendable
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { WHATSAPP_FLOWS_FLAG } from 'dashboard/helper/kaptionWhatsappFlows';
import {
  filterTemplates,
  getAvailableCategories,
  getAvailableLanguages,
  getTemplateSupport,
  groupGalleryTemplates,
  isRumoTemplate,
  parseRumoTemplate,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
// NOTICE: KAPTION-INTEGRATION - template-gallery-rumo (Rumo filter + favorites)
import { useKaptionRumoTemplates } from 'dashboard/components-next/NewConversationModal/useKaptionRumoTemplates';

const props = defineProps({
  templates: { type: Array, default: () => [] },
  selectedTemplate: { type: Object, default: null },
  canCreate: { type: Boolean, default: false },
  // template-gallery-rumo — which inbox the templates belong to (favorites,
  // Rumo version) and the open conversation's language, if any.
  inboxId: { type: [Number, String], default: null },
  conversationLanguage: { type: String, default: '' },
});

const emit = defineEmits(['select', 'back', 'create']);

const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { t, locale } = useI18n();

const {
  enabled: rumoEnabled,
  rumo,
  favorites,
  isFavorite,
  toggleFavorite,
} = useKaptionRumoTemplates({
  inboxId: computed(() => props.inboxId),
  templates: computed(() => props.templates),
  conversationLanguage: computed(() => props.conversationLanguage),
});

const query = ref('');
const activeCategory = ref('ALL');
const activeLanguage = ref('ALL');
const activeStatus = ref('ALL');
// template-gallery-rumo.FILTER.1 — 'ALL' or 'RUMO' (the Rumo pill).
const activeSource = ref('ALL');
const fullPreviewTemplate = ref(null);
const visiblePreviewKeys = ref(new Set());
const previewObservers = new WeakMap();

const previewKey = template => `${template.name}-${template.language}`;
const showPreview = key => {
  if (visiblePreviewKeys.value.has(key)) return;
  visiblePreviewKeys.value = new Set([...visiblePreviewKeys.value, key]);
};

// new-conversation-modal.GALLERY.3 — Mount remote preview iframes only as their cards approach the scroll viewport.
// A large account can expose hundreds of templates; eager mounting made every
// iframe compete for the main thread and occasionally lose initialization.
const vLazyPreview = {
  mounted(element, { value }) {
    if (typeof IntersectionObserver === 'undefined') {
      showPreview(value);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        showPreview(value);
        observer.disconnect();
        previewObservers.delete(element);
      },
      {
        // The scroll container, not the section grid: a grid inside a section
        // never clips, so every card would count as visible.
        root: element.closest('[data-gallery-scroll]') || element.parentElement,
        rootMargin: '120px 0px',
        threshold: 0.01,
      }
    );
    previewObservers.set(element, observer);
    observer.observe(element);
  },
  unmounted(element) {
    previewObservers.get(element)?.disconnect();
    previewObservers.delete(element);
  },
};

const languageName = code => {
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames([locale.value.replace('_', '-')], {
      type: 'language',
    }).of(code.replace('_', '-'));
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch (error) {
    return code;
  }
};

const categories = computed(() =>
  getAvailableCategories(props.templates, rumo.value)
);
const languages = computed(() =>
  getAvailableLanguages(props.templates, rumo.value)
);
const languageOptions = computed(() => [
  {
    value: 'ALL',
    label: t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL_LANGUAGES'),
  },
  ...languages.value.map(language => ({
    value: language,
    // template-gallery-rumo.LANG.3 — "Español", not "es".
    label: rumoEnabled.value ? languageName(language) : language,
  })),
]);

const filtered = computed(() =>
  filterTemplates(props.templates, {
    query: query.value,
    category: activeCategory.value,
    language: activeLanguage.value,
    status: activeStatus.value,
    source: activeSource.value,
    rumo: rumo.value,
  })
);

// template-gallery-rumo.LIST.4 — Favoritas, Tus plantillas, Plantillas Rumo.
const sections = computed(() =>
  groupGalleryTemplates(filtered.value, {
    favorites: favorites.value,
    rumoVersion: rumo.value?.version,
  })
);

// template-gallery-rumo.FILTER.3 — the language the Rumo pill picked on its
// own is given back when the agent leaves it.
let languageBeforeRumo = null;
const pickCategory = category => {
  if (activeSource.value === 'RUMO' && languageBeforeRumo !== null) {
    activeLanguage.value = languageBeforeRumo;
  }
  languageBeforeRumo = null;
  activeSource.value = 'ALL';
  activeCategory.value = category;
};
// template-gallery-rumo.FILTER.2 — the Rumo pill shows Rumo templates in the
// active language; the language filter then shows that language so the
// agent can switch it.
const pickRumo = () => {
  if (activeSource.value === 'RUMO') return;
  activeSource.value = 'RUMO';
  activeCategory.value = 'ALL';
  if (activeLanguage.value === 'ALL') {
    languageBeforeRumo = 'ALL';
    activeLanguage.value = rumo.value.language;
  }
};

const rumoLanguageName = computed(() =>
  languageName(
    activeLanguage.value !== 'ALL' ? activeLanguage.value : rumo.value?.language
  )
);
const sectionTitle = key => {
  if (key === 'RUMO') {
    return t('NEW_CONVERSATION_MODAL.TEMPLATE.SECTION.RUMO', {
      language: rumoLanguageName.value,
    });
  }
  return t(`NEW_CONVERSATION_MODAL.TEMPLATE.SECTION.${key}`);
};

const isRumo = template =>
  Boolean(rumo.value) && isRumoTemplate(template, rumo.value.version);
// "Recordatorio 4" instead of kaption_v2_reminder_r4_att1_es.
const cardTitle = template => {
  if (!isRumo(template)) return template.name;
  const { type, variant } = parseRumoTemplate(template);
  return `${t(`NEW_CONVERSATION_MODAL.TEMPLATE.RUMO_TYPE.${type.toUpperCase()}`)} ${variant}`;
};
const cardSubtitle = template =>
  isRumo(template)
    ? `${template.language} · ${template.name}`
    : template.language;
const KNOWN_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const categoryBadge = template => {
  const category = String(template?.category || 'UTILITY').toUpperCase();
  return KNOWN_CATEGORIES.includes(category)
    ? t(`NEW_CONVERSATION_MODAL.TEMPLATE.CATEGORY.${category}`)
    : category;
};
const isApproved = template =>
  String(template?.status).toUpperCase() === 'APPROVED';
const statusBadge = template => {
  const status = String(template?.status || 'PENDING').toUpperCase();
  if (status === 'APPROVED')
    return t('NEW_CONVERSATION_MODAL.TEMPLATE.APPROVED');
  if (status === 'PENDING') return t('NEW_CONVERSATION_MODAL.TEMPLATE.PENDING');
  return status;
};
const fullPreviewMeta = computed(() => {
  if (!fullPreviewTemplate.value) return '';
  return [
    fullPreviewTemplate.value.language,
    categoryBadge(fullPreviewTemplate.value),
  ]
    .filter(Boolean)
    .join(' · ');
});

// reply-template-picker.TYPES.6 — shapes the agent cannot fill yet stay
// visible but disabled, with the reason, and are never selected.
const unsupportedReason = template => {
  const { supported, reason } = getTemplateSupport(template, {
    allowFlow: isKaptionFlagEnabled(WHATSAPP_FLOWS_FLAG),
  });
  return supported ? '' : t(`KAPTION_REPLY_TEMPLATE.UNSUPPORTED.${reason}`);
};
const selectTemplate = template => {
  if (unsupportedReason(template)) return;
  emit('select', template);
};
const openFullPreview = template => {
  fullPreviewTemplate.value = template;
};
const closeFullPreview = () => {
  fullPreviewTemplate.value = null;
};
</script>

<template>
  <div class="flex flex-col gap-4 min-h-[24rem]">
    <!-- Search + language. Shared Kaption controls keep icon/placeholder
         alignment stable inside Warm Paper and the classic layout. -->
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
      <KaptionSearchInput
        v-model="query"
        data-testid="template-search"
        size="md"
        width-class="w-full"
        :debounce="0"
        :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.SEARCH')"
      />
      <KaptionFilterSelect
        v-if="languages.length > 1"
        v-model="activeLanguage"
        class="shrink-0"
        align="end"
        :options="languageOptions"
        :placeholder="t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL_LANGUAGES')"
        icon="i-lucide-languages"
      />
      <KaptionFilterSelect
        v-model="activeStatus"
        class="shrink-0"
        align="end"
        :searchable="false"
        :options="[
          {
            value: 'ALL',
            label: t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL_STATUSES'),
          },
          {
            value: 'APPROVED',
            label: t('NEW_CONVERSATION_MODAL.TEMPLATE.APPROVED'),
          },
          {
            value: 'PENDING',
            label: t('NEW_CONVERSATION_MODAL.TEMPLATE.PENDING'),
          },
        ]"
        icon="i-lucide-badge-check"
      />
    </div>

    <!-- Category segmented filter + create action share one row; the action
         wraps under the filter only when the modal is too narrow. -->
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div
        class="inline-flex flex-wrap gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <button
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          :class="
            activeCategory === 'ALL' && activeSource === 'ALL'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black4'
          "
          @click="pickCategory('ALL')"
        >
          {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.ALL') }}
        </button>
        <button
          v-for="cat in categories"
          :key="cat"
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          :class="
            activeCategory === cat && activeSource === 'ALL'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black4'
          "
          @click="pickCategory(cat)"
        >
          {{ t(`NEW_CONVERSATION_MODAL.TEMPLATE.CATEGORY.${cat}`) }}
        </button>
        <!-- NOTICE: KAPTION-INTEGRATION - template-gallery-rumo.FILTER.1 -->
        <button
          v-if="rumoEnabled && rumo"
          type="button"
          data-testid="rumo-filter"
          class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          :class="
            activeSource === 'RUMO'
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black4'
          "
          @click="pickRumo"
        >
          {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.RUMO') }}
        </button>
      </div>

      <!-- new-conversation-modal.GALLERY.8 — full label, never truncated. -->
      <Button
        v-if="canCreate"
        :label="t('NEW_CONVERSATION_MODAL.TEMPLATE.CREATE')"
        icon="i-lucide-plus"
        size="sm"
        class="shrink-0 whitespace-nowrap"
        data-testid="create-template-entry"
        @click="emit('create')"
      />
    </div>

    <!-- Gallery grid. The iframe is the card: users choose the same rendered
         WhatsApp preview they will customize in the next step. -->
    <div
      v-if="filtered.length"
      data-gallery-scroll
      class="flex flex-col flex-1 gap-3 overflow-y-auto max-h-[31rem] pr-1"
    >
      <section
        v-for="section in sections"
        :key="section.key"
        class="flex flex-col gap-2"
        :data-testid="`template-section-${section.key}`"
      >
        <h4
          v-if="rumoEnabled"
          class="flex items-center gap-1.5 m-0 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ sectionTitle(section.key) }}
          <span
            class="px-1.5 rounded-full bg-n-alpha-black2 text-n-slate-11 tabular-nums"
          >
            {{ section.items.length }}
          </span>
        </h4>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <article
            v-for="template in section.items"
            :key="`${template.name}-${template.language}`"
            v-lazy-preview="previewKey(template)"
            data-testid="template-card"
            :data-rumo="isRumo(template) ? 'true' : undefined"
            class="relative flex flex-col overflow-hidden transition-all border rounded-xl bg-n-solid-1 hover:border-n-strong hover:shadow-md"
            :class="[
              selectedTemplate?.name === template.name
                ? 'border-n-brand ring-1 ring-n-brand'
                : 'border-n-weak',
              { 'opacity-60 hover:shadow-none': unsupportedReason(template) },
            ]"
            :data-unsupported="unsupportedReason(template) ? 'true' : undefined"
          >
            <button
              type="button"
              class="absolute inset-0 z-20 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand disabled:cursor-not-allowed"
              :aria-label="template.name"
              :disabled="!!unsupportedReason(template)"
              :title="unsupportedReason(template) || undefined"
              @click="selectTemplate(template)"
            />

            <div
              class="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-3 py-2.5 border-b border-n-weak"
            >
              <span class="flex-1 min-w-0">
                <span
                  class="block text-sm font-semibold truncate text-n-slate-12"
                >
                  {{ cardTitle(template) }}
                </span>
                <span
                  class="block text-[11px] tracking-wide truncate text-n-slate-9"
                  :class="{ uppercase: !isRumo(template) }"
                >
                  {{ cardSubtitle(template) }}
                </span>
              </span>
              <!-- NOTICE: KAPTION-INTEGRATION - template-gallery-rumo.LIST.6:
                   with the Rumo gallery on, badges get their own line so the
                   template name is never cut short. -->
              <span
                class="flex items-center gap-1 shrink-0"
                :class="{ 'order-last basis-full': rumoEnabled }"
              >
                <!-- NOTICE: KAPTION-INTEGRATION - template-gallery-rumo.LIST.5 -->
                <span
                  v-if="isRumo(template)"
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-iris-3 text-n-iris-11"
                >
                  {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.RUMO') }}
                </span>
                <span
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded"
                  :class="
                    isApproved(template)
                      ? 'bg-n-green-3 text-n-green-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{ statusBadge(template) }}
                </span>
                <span
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-alpha-black2 text-n-slate-11"
                >
                  {{ categoryBadge(template) }}
                </span>
              </span>
              <span class="flex items-center gap-1 shrink-0">
                <!-- NOTICE: KAPTION-INTEGRATION - template-gallery-rumo.FAVORITES.1 -->
                <button
                  v-if="rumoEnabled && inboxId"
                  type="button"
                  data-testid="favorite-button"
                  class="relative z-30 flex items-center justify-center p-0 transition-colors border rounded-md size-7 border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-black2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :class="
                    isFavorite(template)
                      ? 'text-n-amber-10'
                      : 'text-n-slate-10 hover:text-n-slate-12'
                  "
                  :aria-pressed="isFavorite(template) ? 'true' : 'false'"
                  :aria-label="
                    isFavorite(template)
                      ? t('NEW_CONVERSATION_MODAL.TEMPLATE.UNFAVORITE')
                      : t('NEW_CONVERSATION_MODAL.TEMPLATE.FAVORITE')
                  "
                  :title="
                    isFavorite(template)
                      ? t('NEW_CONVERSATION_MODAL.TEMPLATE.UNFAVORITE')
                      : t('NEW_CONVERSATION_MODAL.TEMPLATE.FAVORITE')
                  "
                  @click.stop="toggleFavorite(template)"
                >
                  <svg
                    aria-hidden="true"
                    class="size-4"
                    viewBox="0 0 24 24"
                    :fill="isFavorite(template) ? 'currentColor' : 'none'"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  data-testid="full-preview-button"
                  class="relative z-30 flex items-center justify-center p-0 transition-colors border rounded-md size-7 border-n-weak bg-n-solid-1 text-n-slate-10 hover:border-n-strong hover:bg-n-alpha-black2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :aria-label="
                    t('NEW_CONVERSATION_MODAL.TEMPLATE.VIEW_FULL', {
                      name: template.name,
                    })
                  "
                  :title="t('NEW_CONVERSATION_MODAL.TEMPLATE.VIEW_FULL_SHORT')"
                  @click.stop="openFullPreview(template)"
                >
                  <!-- Kaption Style Guide: Eye (Privacy), inline SVG. -->
                  <svg
                    aria-hidden="true"
                    class="size-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
                    />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </span>
            </div>

            <div
              class="h-[15rem] overflow-hidden pointer-events-none bg-n-alpha-black1"
              aria-hidden="true"
            >
              <KaptionWhatsAppPreviewEnhanced
                v-if="visiblePreviewKeys.has(previewKey(template))"
                :template="template"
                bare
                truncate
                mode="render"
                class="h-full !rounded-none"
              />
              <div
                v-else
                class="flex items-center justify-center h-full bg-n-alpha-black1"
                data-testid="preview-placeholder"
              >
                <span
                  class="border-2 rounded-full size-5 animate-spin border-n-weak border-t-n-brand"
                />
              </div>
            </div>
            <p
              v-if="unsupportedReason(template)"
              class="flex items-start gap-1.5 px-3 py-2 text-xs border-t border-n-weak bg-n-amber-2 text-n-amber-11"
              data-testid="template-unsupported"
            >
              <span class="i-lucide-ban mt-0.5 size-3.5 shrink-0" />
              {{ unsupportedReason(template) }}
            </p>
          </article>
        </div>
      </section>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 gap-2 py-10 text-center"
      data-testid="template-empty"
    >
      <span class="i-lucide-layout-template size-8 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.EMPTY') }}
      </p>
    </div>

    <!-- Footer -->
    <div class="flex justify-between pt-2">
      <Button
        :label="t('NEW_CONVERSATION_MODAL.BACK')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('back')"
      />
    </div>

    <teleport to="body">
      <div
        v-if="fullPreviewTemplate"
        class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-n-alpha-black2 backdrop-blur-[6px]"
        data-testid="full-preview-dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="
          t('NEW_CONVERSATION_MODAL.TEMPLATE.FULL_PREVIEW', {
            name: fullPreviewTemplate.name,
          })
        "
        @click.self="closeFullPreview"
      >
        <section
          class="flex w-full max-w-2xl max-h-[calc(100vh-2rem)] flex-col overflow-hidden border shadow-2xl rounded-xl border-n-strong bg-n-alpha-3 backdrop-blur-[100px]"
        >
          <header
            class="flex items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
          >
            <div class="min-w-0">
              <h3 class="text-sm font-semibold truncate text-n-slate-12">
                {{ fullPreviewTemplate.name }}
              </h3>
              <p class="text-[11px] uppercase tracking-wide text-n-slate-9">
                {{ fullPreviewMeta }}
              </p>
            </div>
            <Button
              :label="t('NEW_CONVERSATION_MODAL.TEMPLATE.CLOSE_PREVIEW')"
              variant="ghost"
              color="slate"
              size="sm"
              data-testid="full-preview-close"
              @click="closeFullPreview"
            />
          </header>

          <div class="min-h-0 flex-1 p-4 bg-n-alpha-black1">
            <KaptionWhatsAppPreviewEnhanced
              data-testid="full-preview-renderer"
              :template="fullPreviewTemplate"
              bare
              :truncate="false"
              mode="render"
              class="h-[min(72vh,44rem)] min-h-[28rem] border rounded-xl border-n-weak"
            />
          </div>
        </section>
      </div>
    </teleport>
  </div>
</template>
